import { h as __h } from "preact";
import { Component, h } from "preact";
import { machineResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./RenameMachineModal.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./RenameMachineModal.module.css", import.meta.url);
const css = adopt(sheet);

/** @typedef {{ id: string; name: string }} Machine */

/**
 * @param {Machine} machine
 * @returns {Promise<machineResponse | undefined>} The renamed machine, or undefined on dismissal
 */
export const renameMachineModal = machine => modal({
  title: machine.name ? `Rename ${machine.name}` : "Rename",
  content: (/** @type {(result?: machineResponse) => void} */resolve) => h(ModalChildren, {
    resolve,
    machine
  })
});

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {Machine} machine
 * @property {(result?: machineResponse) => void} resolve
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const json = parse(object({
      name: string
    }), Object.fromEntries(form.entries()));
    const {
      machine
    } = this.props;
    const {
      data
    } = await authedApiRequest("PATCH", `/v2/machines/${machine.id}`, {
      json,
      schema: response(machineResponse)
    });
    alert(`Renamed ${machine.name} to ${data.name}`, {
      type: "success"
    });
    this.props.resolve(data);
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    const {
      machine
    } = this.props;
    return __h("form", {
      "class": css.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Name",
      error: error?.fields.name?.message,
      validity: {
        tooShort: "Names must be at least two characters long."
      }
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      minlength: 2,
      maxlength: 100,
      placeholder: machine.name,
      autofocus: true
    })), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Rename"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}